import type { AppRouter } from "@sp-crm/server/rpc";
import type { inferProcedureOutput } from "@trpc/server";
import { InputFormBlock } from "components/app/input-form-block";
import { AdditionalContactsHeader } from "components/clients/show-client/care-providers/additional-contacts-header";
import { CareProvidersHeader } from "components/clients/show-client/care-providers/care-providers-header";
import { HomeCareLeadContactRelationship } from "generated/graphql";
import * as React from "react";
import { breakNewlines } from "util/text";
import { present, SharedContactDetails } from "./shared-contact-details";

export type SharedAdditionalContact = inferProcedureOutput<
    AppRouter["sharedPipeline"]["lead"]
>["additionalContacts"][number];

const AdditionalContactCard: React.FC<{ contact: SharedAdditionalContact }> = ({
    contact,
}) => (
    <li className="border border-gray-200 rounded-lg p-4 text-sm">
        <div className="text-base">{present(contact.name) ?? "(no name)"}</div>
        {present(contact.role) ? (
            <div className="font-medium mb-1">{contact.role}</div>
        ) : null}
        {present(contact.relationship) ? (
            <div className="font-medium mb-1">{contact.relationship}</div>
        ) : null}
        <SharedContactDetails contact={contact} />
        {contact.powerOfAttorney ? (
            <div className="mt-2">
                <span>Power of Attorney: </span>
                <span className="font-medium">{contact.powerOfAttorney}</span>
            </div>
        ) : null}
        {contact.notes ? (
            <p className="mt-2 italic">{breakNewlines(contact.notes)}</p>
        ) : null}
    </li>
);

const ContactSection: React.FC<{
    header: React.ReactNode;
    testId: string;
    contacts: SharedAdditionalContact[];
}> = ({ header, testId, contacts }) => (
    <InputFormBlock>
        <div className="space-y-4" data-testid={testId}>
            <div className="flex items-center justify-between">{header}</div>
            <ul className="2xl:grid 2xl:grid-cols-2 2xl:gap-4 mt-4 space-y-4 2xl:space-y-0">
                {contacts.map((contact, index) => (
                    <AdditionalContactCard
                        key={`${contact.kind}-${index}`}
                        contact={contact}
                    />
                ))}
            </ul>
        </div>
    </InputFormBlock>
);

export const SharedAdditionalContacts: React.FC<{
    contacts: SharedAdditionalContact[];
}> = ({ contacts }) => (
    <>
        <ContactSection
            header={<AdditionalContactsHeader />}
            testId="shared-additional-contacts"
            contacts={contacts.filter(
                contact => contact.kind === HomeCareLeadContactRelationship.MoreContact,
            )}
        />
        <ContactSection
            header={<CareProvidersHeader />}
            testId="shared-care-providers"
            contacts={contacts.filter(
                contact => contact.kind === HomeCareLeadContactRelationship.CareProvider,
            )}
        />
    </>
);
